import { useEffect, useId } from 'react';
import { ApiError, api } from '@/api/client';
import { errorMessage } from '@/components/ui';
import { useUnsaved } from '@/stores/unsaved';

/**
 * Small helpers the Attendance and Leave screens share.
 */

/** PUT an HR route: the attendance register and the typed-in month. */
export function hrPut<T>(path: string, payload: unknown): Promise<T> {
  return api.put<T>(path, payload);
}

/**
 * Every problem the server listed, one per line. Validation and batch checks
 * answer with a list; anything else is a single message.
 */
export function problemList(error: unknown, fallback: string): string[] {
  if (error instanceof ApiError && typeof error.body === 'object' && error.body !== null) {
    const raw = (error.body as { message?: unknown }).message;
    if (Array.isArray(raw) && raw.length > 0) return raw.map(String);
  }
  return [errorMessage(error, fallback)];
}

/**
 * Tells the app this screen holds unsaved work while `dirty` is true, so
 * leaving the screen, signing out or closing the window asks first. The
 * same store FormPanel's useTrackUnsaved feeds, but driven by the screen's
 * own state: marks set with a button or a key press fire no input event,
 * and saving must clear the mark while the screen stays open.
 */
export function useUnsavedFlag(dirty: boolean): void {
  const id = useId();
  useEffect(() => {
    useUnsaved.getState().mark(id, dirty);
  }, [id, dirty]);
  useEffect(() => () => useUnsaved.getState().mark(id, false), [id]);
}

/** A few years either side of now, newest first, for a year picker. */
export function yearOptions(around: number, back = 3, ahead = 1): Array<{ value: string; label: string }> {
  const out: Array<{ value: string; label: string }> = [];
  for (let y = around + ahead; y >= around - back; y--) out.push({ value: String(y), label: String(y) });
  return out;
}

/** Days as plain text: "3", "1.5", never "3.00". */
export function daysText(value: string | number | null | undefined): string {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '—';
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}
